import { default as React } from 'react';
import { Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  // EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  // EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';

import { isSnykAvailable } from 'backstage-plugin-snyk';

import sisenseDashboard from './utils/sisenseDashboardData.ts';

import {
  CustomSnykOverview,
  SisenseSolutionPage as SisenseSolution,
  SisenseTeamPage as SisenseTeam,
} from './index';

import {
  MicroappAboutCard,
  MicroappAvailabilityCard,
  MicroappSecondaryCard,
  MicroappArgoCDCard,
  MicroappEnvironmentsCard,
  SecretsTab,
  AccessTab,
  BackupsTab,
  TelemetryTab,
} from './microapps';

import {
  useCurrentUserEmail,
  isUserAuthorizedForEntity,
  useSkyStageAdminEmails,
  useMicroappEnvironmentStatus,
} from './microapps/hooks';

import { useEntity } from '@backstage/plugin-catalog-react';

import {
  EntityJiraOverviewCard,
  EntityJiraQueryCard,
  isJiraAvailable,
  hasJiraQuery,
} from '@roadiehq/backstage-plugin-jira';

import {
  EntityArgoCDOverviewCard,
  isArgocdAvailable,
} from '@roadiehq/backstage-plugin-argo-cd';

import {
  EntityAzurePipelinesContent,
  isAzurePipelinesAvailable,
} from '@backstage-community/plugin-azure-devops';
import { AzurePipelinesLogInterceptor } from './AzurePipelinesWrapper';

import {
  isNewRelicDashboardAvailable,
  EntityNewRelicDashboardContent,
  EntityNewRelicDashboardCard,
} from '@backstage-community/plugin-newrelic-dashboard';

import { JsmEntityDetailsCard } from './JsmEntityDetailsCard.tsx';
import { SystemAboutCard } from './SystemAboutCard';
import { DomainAboutCard } from './DomainAboutCard';
import { DomainComponentsCard } from './DomainComponentsCard';
import { McpServerEntityPage } from '../McpServers';
import {
  EntityTechStackCard,
  SystemTechStackCard,
  EntityExternalServicesCard,
} from '@internal/plugin-repo-lens-frontend';
import { PublicAppEntityPage } from './public-apps/PublicAppEntityPage';

const MICROAPP_ANNOTATION_KEY = 'skystage.wellsky.com/type';
const MICROAPP_ANNOTATION_VALUE = 'microapp';

// Helper functions to check if cards should be displayed
const hasLinks = (entity: any) => {
  return Boolean(entity.metadata?.links && entity.metadata.links.length > 0);
};

const hasSubcomponents = (entity: any) => {
  return (
    entity.relations?.some(
      (relation: any) => relation.type === RELATION_HAS_PART,
    ) || false
  );
};

const hasJsmData = (entity: any) => {
  return Boolean(entity.metadata?.annotations?.['jsm/engineering-team']);
};

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    <EntitySwitch.Case if={isAzurePipelinesAvailable}>
      <>
        <AzurePipelinesLogInterceptor />
        <EntityAzurePipelinesContent defaultLimit={25} />
      </>
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}

    {/* About Card - Always shown on the left */}
    <Grid item md={6} xs={12}>
      <MicroappAboutCard variant="gridItem" />
    </Grid>

    {/* Right side card - Context-dependent */}
    <EntitySwitch>
      {/* For Microapps: Config card (creator) or ArgoCD card (non-creator) */}
      <EntitySwitch.Case
        if={entity =>
          entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
          MICROAPP_ANNOTATION_VALUE
        }
      >
        <MicroappSecondaryCard variant="gridItem" />
      </EntitySwitch.Case>

      {/* For Regular Projects: Catalog Graph */}
      <EntitySwitch.Case
        if={entity => !entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY]}
      >
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    {/* Microapp-specific cards below */}
    <EntitySwitch>
      <EntitySwitch.Case
        if={entity =>
          entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
          MICROAPP_ANNOTATION_VALUE
        }
      >
        {/* IDP-983: Environments widget (Staging + Production deployment
            controls) — shown above the ArgoCD card. Admin/owner-only (gated
            inside the component). */}
        <Grid item md={12} xs={12}>
          <MicroappEnvironmentsCard />
        </Grid>

        {/* ArgoCD card for Hosted apps (only shown for creators - non-creators see it beside About) */}
        <MicroappArgoCDCard />

        {/* Platform Uptime card for Microapps (IDP-887) */}
        <Grid item md={6} xs={12}>
          <MicroappAvailabilityCard />
        </Grid>

        {/* Snyk Overview for Microapps */}
        <EntitySwitch>
          <EntitySwitch.Case if={isSnykAvailable}>
            <Grid item md={6} xs={12}>
              <CustomSnykOverview />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </EntitySwitch.Case>

      {/* Regular Project cards below */}
      <EntitySwitch.Case
        if={entity => !entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY]}
      >
        <EntitySwitch>
          <EntitySwitch.Case if={hasLinks}>
            <Grid item md={4} xs={12}>
              <EntityLinksCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>

        <EntitySwitch>
          <EntitySwitch.Case if={hasSubcomponents}>
            <Grid item md={8} xs={12}>
              {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
              <EntityHasSubcomponentsCard variant="gridItem" />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>

        {/* Snyk for Regular Projects */}
        <EntitySwitch>
          <EntitySwitch.Case if={isSnykAvailable}>
            <Grid item md={6} xs={12}>
              <CustomSnykOverview />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>

        {/* ArgoCD for Regular Projects */}
        <EntitySwitch>
          <EntitySwitch.Case if={e => Boolean(isArgocdAvailable(e))}>
            <Grid item md={6} xs={12}>
              <EntityArgoCDOverviewCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>

        {/* Tech Stack Card for Regular Projects */}
        <Grid item xs={12}>
          <EntityTechStackCard />
        </Grid>
        <Grid item xs={12}>
          <EntityExternalServicesCard filterType="component" />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    {/* Common cards for all project types */}
    <EntitySwitch>
      <EntitySwitch.Case if={isJiraAvailable}>
        <Grid item md={12} xs={12}>
          <EntityJiraOverviewCard />
        </Grid>
      </EntitySwitch.Case>
      <EntitySwitch.Case if={hasJiraQuery}>
        <Grid item md={12} xs={12}>
          <EntityJiraQueryCard title="Our Custom Query" />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={isNewRelicDashboardAvailable}>
        <Grid item md={6} xs={12}>
          <EntityNewRelicDashboardCard />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/ci-cd"
      title="CI/CD"
      if={entity =>
        entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] !==
        MICROAPP_ANNOTATION_VALUE
      }
    >
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6} xs={12}>
          <EntityProvidedApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityConsumedApisCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/dependencies"
      title="Dependencies"
      if={entity =>
        entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] !==
        MICROAPP_ANNOTATION_VALUE
      }
    >
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/docs"
      title="Docs"
      if={entity =>
        entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] !==
        MICROAPP_ANNOTATION_VALUE
      }
    >
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isNewRelicDashboardAvailable}
      path="/newrelic-dashboard"
      title="New Relic"
    >
      <EntityNewRelicDashboardContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/backups"
      title="Backups"
      if={entity =>
        entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
          MICROAPP_ANNOTATION_VALUE &&
        entity.metadata.annotations?.['microapp/persistentStorage'] === 'true'
      }
    >
      <BackupsTab />
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * WebsiteEntityPage renders the entity layout for website-type components.
 *
 * Implemented as a React component (not a JSX constant) so that hooks can be
 * used to compute ownership state. The Secrets tab is only shown to app owners,
 * determined by checking the `microapp/owners` and `microapp/createdBy`
 * annotations on the catalog entity (written by the microapp scaffolder into
 * the entity's backstage.yaml).
 *
 * `isOwner` starts as false while the identity API resolves the current user's
 * email. Once resolved, if the user is an owner the tab becomes visible and
 * EntityLayout re-renders to show it in the nav.
 */
const WebsiteEntityPage = () => {
  const { entity } = useEntity();
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();
  // Reads microapp/owners (comma-separated emails) and microapp/createdBy
  // from the catalog entity's annotations — both written to backstage.yaml
  // by the microapp scaffolder during app creation. SkyStage admins
  // (catalogApi.admin.allowedEmails) are treated as owners on every microapp.
  const isOwner = isUserAuthorizedForEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );

  // IDP-983: Backups are PRODUCTION-ONLY. The catalog entity can reflect the
  // staging deployment (the GitHub discovery provider uses "staging always
  // wins"), so the entity's stagingOnly/namespace annotations are unreliable for
  // this decision. Use the live ArgoCD environment status instead — the Backups
  // tab appears only when production is actually deployed (i.e. after promote).
  const { status: envStatus } = useMicroappEnvironmentStatus(
    entity.metadata.name,
  );
  const productionDeployed = envStatus?.production?.deployed ?? false;

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {overviewContent}
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/ci-cd"
        title="CI/CD"
        if={e =>
          e.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] !==
          MICROAPP_ANNOTATION_VALUE
        }
      >
        {cicdContent}
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/kubernetes"
        title="Kubernetes"
        if={isKubernetesAvailable}
      >
        <EntityKubernetesContent />
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/dependencies"
        title="Dependencies"
        if={e =>
          e.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] !==
          MICROAPP_ANNOTATION_VALUE
        }
      >
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6}>
            {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
            <EntityDependsOnComponentsCard variant="gridItem" />
          </Grid>
          <Grid item md={6}>
            {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
            <EntityDependsOnResourcesCard variant="gridItem" />
          </Grid>
        </Grid>
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/docs"
        title="Docs"
        if={e =>
          e.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] !==
          MICROAPP_ANNOTATION_VALUE
        }
      >
        {techdocsContent}
      </EntityLayout.Route>

      {/* Secrets tab: only visible to microapp owners (reads microapp/owners
          and microapp/createdBy annotations from the entity's backstage.yaml).
          isOwner is false while the identity API is resolving, then updates
          when the current user's email is known. */}
      <EntityLayout.Route
        path="/secrets"
        title="Secrets"
        if={e =>
          e.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
            MICROAPP_ANNOTATION_VALUE && isOwner
        }
      >
        <SecretsTab />
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/access"
        title="Manage Access"
        if={e =>
          e.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
            MICROAPP_ANNOTATION_VALUE &&
          isOwner &&
          e.metadata.annotations?.['microapp/visibility'] ===
            'Custom (Specific Users)'
        }
      >
        <AccessTab />
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/backups"
        title="Backups"
        if={e =>
          e.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
            MICROAPP_ANNOTATION_VALUE &&
          e.metadata.annotations?.['microapp/persistentStorage'] === 'true' &&
          // IDP-983: Backups are production-only. Show the tab only when
          // production is live (after promote) — never for a staging-only app.
          productionDeployed
        }
      >
        <BackupsTab />
      </EntityLayout.Route>

      {/* IDP-939: Telemetry tab for microapp owners */}
      <EntityLayout.Route
        path="/telemetry"
        title="Telemetry"
        if={e =>
          e.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
            MICROAPP_ANNOTATION_VALUE && isOwner
        }
      >
        <TelemetryTab />
      </EntityLayout.Route>
    </EntityLayout>
  );
};

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('mcp-server')}>
      {McpServerEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('public-microapp')}>
      <PublicAppEntityPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      <WebsiteEntityPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={hasLinks}>
            <Grid item md={4} xs={12}>
              <EntityLinksCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <Grid item md={6} xs={12}>
          <EntityProvidingComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityConsumingComponentsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={hasLinks}>
            <Grid item xs={12} md={6}>
              <EntityLinksCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <Grid item xs={12}>
          <SisenseTeam dashboardOid="63a352d1dae09d0036b4a500" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <EntitySwitch>
          <EntitySwitch.Case if={hasJsmData}>
            <Grid item md={5} xs={12}>
              <SystemAboutCard variant="gridItem" />
            </Grid>
            <Grid item md={7} xs={12}>
              <JsmEntityDetailsCard variant="gridItem" />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
          <EntitySwitch.Case if={entity => !hasJsmData(entity)}>
            <Grid item md={5} xs={12} style={{ alignSelf: 'flex-start' }}>
              <SystemAboutCard variant="gridItem" />
            </Grid>
            <Grid item md={7} xs={12}>
              {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
              <EntityHasComponentsCard variant="gridItem" />
            </Grid>
          </EntitySwitch.Case>
          <EntitySwitch.Case if={hasJsmData}>
            <Grid item md={12} xs={12}>
              {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
              <EntityHasComponentsCard variant="gridItem" />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
        {/* <Grid item md={12} xs={12}>
          <Stack spacing={2}>
            <EntityCatalogGraphCard height={400} />
          </Stack>
        </Grid> */}
        {/* <Grid item md={6} xs={12}>
        </Grid> */}

        {/* <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid> */}

        {/* <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid> */}
        {/* <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid> */}
        <Grid item md={12} xs={12}>
          {sisenseDashboard.map(category => (
            <React.Fragment key={category.title}>
              {category.dashboards.map(dashboard => (
                <SisenseSolution
                  key={dashboard.dashboardOid}
                  dashboardOid={dashboard.dashboardOid}
                  tab={category.title}
                  widgetLists={dashboard.widgetLists}
                  isWidgetRequired={dashboard.isWidgetRequired}
                />
              ))}
            </React.Fragment>
          ))}
        </Grid>
        <Grid item md={12} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={12} xs={12}>
          <SystemTechStackCard filterType="system" />
        </Grid>
        <Grid item md={12} xs={12}>
          <EntityExternalServicesCard filterType="system" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntitySwitch>
        <EntitySwitch.Case
          if={entity => !entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY]}
        >
          <EntityCatalogGraphCard
            direction={Direction.TOP_BOTTOM}
            title="Solution Diagram"
            height={700}
            relations={[
              RELATION_PART_OF,
              RELATION_HAS_PART,
              RELATION_API_CONSUMED_BY,
              RELATION_API_PROVIDED_BY,
              RELATION_CONSUMES_API,
              RELATION_PROVIDES_API,
              RELATION_DEPENDENCY_OF,
              RELATION_DEPENDS_ON,
            ]}
            unidirectional={false}
          />
        </EntitySwitch.Case>
      </EntitySwitch>
    </EntityLayout.Route>
    {sisenseDashboard.map((category, idx) => {
      const normalizedPath = `/${category.title
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9\-]/g, '')}`;

      return (
        <EntityLayout.Route
          key={category.title + idx}
          path={normalizedPath}
          title={category.title}
        >
          <>
            {category.dashboards.map(dashboard => (
              <SisenseSolution
                key={dashboard.dashboardOid}
                dashboardOid={dashboard.dashboardOid}
                tab={category.title}
              />
            ))}
          </>
        </EntityLayout.Route>
      );
    })}
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <DomainAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          {/* @ts-expect-error - variant is a deprecated but functional legacy prop */}
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
        <Grid item md={12}>
          <DomainComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={12}>
          <SystemTechStackCard filterType="domain" />
        </Grid>
        <Grid item md={12}>
          <EntityExternalServicesCard filterType="domain" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
